import { useMemo } from 'react'

import { PerimeterCoordinatesResponse } from '@/models/perimeter-coordinates'
import { convertRingToCoordinates } from '@/utils/perimeter-coordinates'

export function useCalculatePerimeter(
  perimeterCoordinates: PerimeterCoordinatesResponse,
) {
  const polygonCoordinates: number[][][] = useMemo(() => {
    const isPolar = perimeterCoordinates[0]?.tipo_coordenada === 'Polar'

    return perimeterCoordinates.flatMap((poly) => {
      return poly.coordenadas.map((ring) =>
        convertRingToCoordinates(ring, isPolar),
      )
    })
  }, [perimeterCoordinates])

  const bounds = useMemo(() => {
    const allCoords = polygonCoordinates.flat()

    return allCoords.reduce(
      (acc, coord) => ({
        west: Math.min(acc.west, coord[0]),
        south: Math.min(acc.south, coord[1]),
        east: Math.max(acc.east, coord[0]),
        north: Math.max(acc.north, coord[1]),
      }),
      {
        west: allCoords[0]?.[0] ?? 0,
        south: allCoords[0]?.[1] ?? 0,
        east: allCoords[0]?.[0] ?? 0,
        north: allCoords[0]?.[1] ?? 0,
      },
    )
  }, [polygonCoordinates])

  const polygonGeoJSON = useMemo(() => {
    return {
      type: 'FeatureCollection' as const,
      features: perimeterCoordinates.map((poly) => {
        const isPolar = poly.tipo_coordenada === 'Polar'
        const coordinates = poly.coordenadas.map((ring) => {
          return convertRingToCoordinates(ring, isPolar)
        })

        return {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates,
          },
        }
      }),
    }
  }, [perimeterCoordinates])

  return { bounds, polygonCoordinates, polygonGeoJSON }
}
